import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "react-toastify";
import usePageTitle from "../helpers/usePageTitle";
import { useModal } from "../components/modal/ModalContext";
import {
  getLinkedInAccounts,
  deleteLinkedInAccount,
  connectLinkedInAccount,
  disconnectLinkedInAccount,
  testLinkedInAccountConnection,
  rotateLinkedInIp,
  retryLinkedInProxyProvision,
} from "../services/apiService";
import LoadingOverlay from "../components/shared/LoadingOverlay";
import DropdownMenu from "../components/shared/DropdownMenu";
import LinkedInAccountModal from "../components/linkedinAccounts/LinkedInAccountModal";
import ConnectLinkedInWizard, {
  unwrapConnectResult,
} from "../components/linkedinAccounts/ConnectLinkedInWizard";
import ChallengeModal from "../components/linkedinAccounts/ChallengeModal";
import { waitForProxyReady } from "../components/linkedinAccounts/proxyReady";
import { getAccountLimits } from "../components/linkedinAccounts/accountState";
import { getErrorMessage, notifyError } from "../helpers/apiError";

// --- small presentational icons, scoped to this page ---
const SearchIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M17 17l-3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ChevronDownIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const KebabIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <circle cx="10" cy="4" r="1.6" />
    <circle cx="10" cy="10" r="1.6" />
    <circle cx="10" cy="16" r="1.6" />
  </svg>
);

const InfoIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="7.3" stroke="currentColor" strokeWidth="1.4" />
    <path d="M10 9.2v4.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="10" cy="6.6" r="0.9" fill="currentColor" />
  </svg>
);

const GearIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.5 3.3h3l.4 1.9c.5.17.94.4 1.35.7l1.85-.68 1.5 2.6-1.47 1.27c.05.28.07.55.07.82s-.02.54-.07.82l1.47 1.27-1.5 2.6-1.85-.68c-.4.3-.85.53-1.35.7l-.4 1.9h-3l-.4-1.9a5.4 5.4 0 01-1.35-.7l-1.85.68-1.5-2.6 1.47-1.27a5.6 5.6 0 010-1.64L4.9 7.82l1.5-2.6 1.85.68c.4-.3.85-.53 1.35-.7l.4-1.9z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinejoin="round"
    />
    <circle cx="10" cy="10" r="2.2" stroke="currentColor" strokeWidth="1.25" />
  </svg>
);

const PersonMiniIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <circle cx="10" cy="6.5" r="3.2" />
    <path d="M3.5 17c0-3.3 3-5.5 6.5-5.5s6.5 2.2 6.5 5.5" />
  </svg>
);

const EnvelopeMiniIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2.5" y="4.5" width="15" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3 5.5l7 5.5 7-5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckMiniIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.5 10.2l3.2 3.2 7.8-7.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SpinnerIcon = ({ className = "w-3 h-3" }) => (
  <svg className={`${className} animate-spin`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const EmptyIllustration = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="158" height="109" viewBox="0 0 158 109" fill="none">
    <g clip-path="url(#clip0_22143_64909)">
      <path d="M75.6699 97.0696C99.9705 97.0696 119.67 77.3701 119.67 53.0696C119.67 28.7691 99.9705 9.06958 75.6699 9.06958C51.3694 9.06958 31.6699 28.7691 31.6699 53.0696C31.6699 77.3701 51.3694 97.0696 75.6699 97.0696Z" fill="#E5E7EB" />
      <g filter="url(#filter0_d_22143_64909)">
        <path d="M75.6699 102.07C95.5522 102.07 111.67 94.4584 111.67 85.0696C111.67 75.6807 95.5522 68.0696 75.6699 68.0696C55.7877 68.0696 39.6699 75.6807 39.6699 85.0696C39.6699 94.4584 55.7877 102.07 75.6699 102.07Z" fill="white" />
      </g>
      <g filter="url(#filter1_d_22143_64909)">
        <path d="M75.1699 64.0696C86.4918 64.0696 95.6699 54.6676 95.6699 43.0696C95.6699 31.4716 86.4918 22.0696 75.1699 22.0696C63.8481 22.0696 54.6699 31.4716 54.6699 43.0696C54.6699 54.6676 63.8481 64.0696 75.1699 64.0696Z" fill="white" />
      </g>
      <path d="M143.119 51.9966H114.635C108.694 51.9966 103.877 56.8132 103.877 62.7547V91.2384C103.877 97.18 108.694 101.997 114.635 101.997H143.119C149.06 101.997 153.877 97.18 153.877 91.2384V62.7547C153.877 56.8132 149.06 51.9966 143.119 51.9966Z" fill="#D1D5DB" />
      <path d="M116.76 66.798C115.881 66.8164 115.031 66.4846 114.395 65.8752C114.086 65.5941 113.839 65.251 113.67 64.8682C113.501 64.4853 113.414 64.0712 113.414 63.6524C113.414 63.2336 113.501 62.8195 113.67 62.4366C113.839 62.0538 114.086 61.7107 114.395 61.4295C115.039 60.8319 115.883 60.5 116.76 60.5C117.636 60.5 118.481 60.8319 119.125 61.4295C119.434 61.7107 119.68 62.0538 119.849 62.4366C120.018 62.8195 120.106 63.2336 120.106 63.6524C120.106 64.0712 120.018 64.4853 119.849 64.8682C119.68 65.251 119.434 65.5941 119.125 65.8752C118.488 66.4842 117.638 66.8159 116.76 66.798ZM113.644 92.1572V69.6934H119.843V92.1572H113.644Z" fill="white" />
      <path d="M130.172 79.1707V92.1572H123.973V69.6934H129.88V73.6569H130.142C130.616 72.3781 131.489 71.2879 132.631 70.5493C133.794 69.7802 135.206 69.3957 136.867 69.3957C138.287 69.3613 139.69 69.7145 140.926 70.4178C142.086 71.1072 143.022 72.1213 143.619 73.3362C144.26 74.5974 144.581 76.1008 144.581 77.8465V92.1502H138.382V78.9585C138.391 77.5835 138.042 76.5068 137.335 75.7286C136.628 74.9503 135.653 74.5658 134.41 74.5751C133.64 74.5595 132.879 74.7465 132.204 75.1172C131.565 75.4814 131.049 76.0283 130.72 76.6884C130.345 77.4606 130.157 78.3113 130.172 79.1707Z" fill="white" />
    </g>
    <defs>
      <filter id="filter0_d_22143_64909" x="33.6699" y="66.0696" width="84" height="46" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix" />
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
        <feOffset dy="4" />
        <feGaussianBlur stdDeviation="3" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_22143_64909" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_22143_64909" result="shape" />
      </filter>
      <filter id="filter1_d_22143_64909" x="48.6699" y="20.0696" width="53" height="54" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix" />
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
        <feOffset dy="4" />
        <feGaussianBlur stdDeviation="3" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_22143_64909" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_22143_64909" result="shape" />
      </filter>
      <clipPath id="clip0_22143_64909">
        <rect width="158" height="109" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const Pill = ({ icon, value }) => (
  <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 text-[12px] font-medium px-2 py-1 rounded-md">
    <span className="text-gray-400">{icon}</span>
    {value}
  </span>
);

const STATUS_STYLES = {
  AVAILABLE: "bg-green-50 text-green-700 border border-green-200",
  CONNECTING: "bg-blue-50 text-blue-600 border border-blue-100",
  PIN_REQUIRED: "bg-purple-50 text-purple-700 border border-purple-100",
  WRONG_CREDENTIALS: "bg-red-50 text-red-700 border border-red-100",
  NOT_CONNECTED: "bg-gray-100 text-gray-600 border border-gray-200",
  PROXY_SETUP: "bg-blue-50 text-blue-600 border border-blue-100",
  PROXY_FAILED: "bg-orange-50 text-orange-700 border border-orange-100",
  SUSPENDED: "bg-yellow-50 text-yellow-700 border border-yellow-100",
  BANNED: "bg-red-50 text-red-700 border border-red-100",
  NEEDS_ATTENTION: "bg-orange-50 text-orange-700 border border-orange-100",
};

const isWrongCredentials = (account) => {
  if (account.lastSuggestedAction === "edit_credentials") return true;
  const err = (account.connectionError || "").toLowerCase();
  return err.includes("credential") || err.includes("invalid") || err.includes("login failed");
};

const getDisplayStatus = (account, isConnecting = false) => {
  if (isConnecting) return "CONNECTING";
  const proxyStatus = account.proxyProvisioningStatus;
  if (proxyStatus === "PENDING" || proxyStatus === "PROVISIONING") return "PROXY_SETUP";
  if (proxyStatus === "FAILED") return "PROXY_FAILED";
  if (account.status === "CONNECTING") return "CONNECTING";
  if (account.status === "PENDING_VERIFICATION") return "PIN_REQUIRED";
  if (account.status === "SUSPENDED") return "SUSPENDED";
  if (account.status === "BANNED") return "BANNED";
  if (account.isConnected && account.status === "ACTIVE") {
    return account.connectionError || account.needsAttention ? "NEEDS_ATTENTION" : "AVAILABLE";
  }
  if (account.lastSuggestedAction === "rotate_ip") return "NOT_CONNECTED";
  if (isWrongCredentials(account)) return "WRONG_CREDENTIALS";
  return "NOT_CONNECTED";
};

const StatusBadge = ({ displayStatus }) => {
  const { t } = useTranslation();
  const colorClass = STATUS_STYLES[displayStatus] || "bg-gray-100 text-gray-600 border border-gray-200";
  const busy = displayStatus === "CONNECTING" || displayStatus === "PROXY_SETUP";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium ${colorClass}`}>
      {busy ? <SpinnerIcon className="w-3 h-3" /> : displayStatus === "AVAILABLE" && <CheckMiniIcon className="w-3 h-3" />}
      {t(`linkedin.displayStatus.${displayStatus}`, { defaultValue: displayStatus })}
    </span>
  );
};

const LinkedInAccounts = () => {
  const { t } = useTranslation();
  usePageTitle("linkedinAccounts");
  const { showModal } = useModal();
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [filters, setFilters] = useState({});
  const [connectingIds, setConnectingIds] = useState(new Set());
  const [actionHints, setActionHints] = useState({});
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const pollRef = useRef(null);

  const statusFilterOptions = [
    { value: "ACTIVE", label: t("linkedin.displayStatus.AVAILABLE") },
    { value: "INACTIVE", label: t("linkedin.displayStatus.NOT_CONNECTED") },
    { value: "CONNECTING", label: t("linkedin.displayStatus.CONNECTING") },
    { value: "PENDING_VERIFICATION", label: t("linkedin.displayStatus.PIN_REQUIRED") },
    { value: "SUSPENDED", label: t("status.SUSPENDED") },
    { value: "BANNED", label: t("status.BANNED") },
  ];

  const fetchAccounts = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      };
      const response = await getLinkedInAccounts(params);
      const resData = response.data;
      if (Array.isArray(resData)) {
        setAccounts(resData);
        setPagination((prev) => ({ ...prev, total: resData.length, totalPages: 1 }));
      } else if (resData?.data) {
        const { data, page, limit, total, totalPages, hasNext, hasPrev } = resData;
        setAccounts(data || []);
        setPagination({
          page: page || 1,
          limit: limit || 10,
          total: total || 0,
          totalPages: totalPages || 1,
          hasNext: !!hasNext,
          hasPrev: !!hasPrev,
        });
      } else {
        setAccounts(Array.isArray(resData) ? resData : []);
      }
    } catch (err) {
      setError(getErrorMessage(err, t("linkedin.toast.error")));
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, t]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const needsPolling = useMemo(
    () =>
      accounts.some(
        (a) =>
          a.status === "CONNECTING"
          || a.status === "PENDING_VERIFICATION"
          || a.proxyProvisioningStatus === "PENDING"
          || a.proxyProvisioningStatus === "PROVISIONING"
          || connectingIds.has(a.id)
      ),
    [accounts, connectingIds]
  );

  useEffect(() => {
    if (!needsPolling) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    pollRef.current = setInterval(() => {
      fetchAccounts(true);
    }, 3000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [needsPolling, fetchAccounts]);

  const applyFilters = (overrides = {}) => {
    const nextSearch = overrides.search ?? searchValue;
    const nextStatus = overrides.status ?? statusFilter;
    setFilters({
      ...(nextSearch ? { search: nextSearch } : {}),
      ...(nextStatus ? { status: nextStatus } : {}),
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Debounce free-text search so there's no separate "Filter" button
  useEffect(() => {
    const handle = setTimeout(() => {
      applyFilters({ search: searchValue });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const markConnecting = (accountId, snapshot) => {
    setConnectingIds((prev) => new Set(prev).add(accountId));
    setAccounts((prev) => {
      const exists = prev.some((a) => a.id === accountId);
      if (exists) {
        return prev.map((a) =>
          a.id === accountId
            ? { ...a, status: "CONNECTING", isConnected: false, connectionError: null }
            : a
        );
      }
      return [
        {
          id: accountId,
          accountName: snapshot?.accountName || snapshot?.email || "—",
          email: snapshot?.email || "",
          status: "CONNECTING",
          isConnected: false,
          ...snapshot,
        },
        ...prev,
      ];
    });
  };

  const clearConnecting = (accountId) => {
    setConnectingIds((prev) => {
      const next = new Set(prev);
      next.delete(accountId);
      return next;
    });
  };

  const setAccountHint = (accountId, hint) => {
    setActionHints((prev) => ({
      ...prev,
      [accountId]: { ...(prev[accountId] || {}), ...hint },
    }));
  };

  const clearAccountHint = (accountId) => {
    setActionHints((prev) => {
      const next = { ...prev };
      delete next[accountId];
      return next;
    });
  };

  const mergeAccount = (account) => ({
    ...account,
    ...(actionHints[account.id] || {}),
  });

  const openPinModal = (accountId, challengeType, suggestedAction) => {
    showModal(
      "linkedin.pinTitle",
      <ChallengeModal
        accountId={accountId}
        challengeType={challengeType}
        suggestedAction={suggestedAction}
        onSuccess={() => {
          clearConnecting(accountId);
          clearAccountHint(accountId);
          fetchAccounts(true);
        }}
      />
    );
  };

  const handleConnectResult = (result, accountId) => {
    clearConnecting(accountId);
    const status = result?.status;
    const suggestedAction = result?.suggestedAction || result?.error?.suggestedAction;
    const challengeType = result?.challengeType || result?.error?.challengeType;
    const errorMessage = result?.error?.message || (typeof result?.error === "string" ? result.error : null);
    // The API refuses to reconnect an account it still flags as connected, even when it has an error.
    // That refusal isn't the account's problem, so keep the real error instead of overwriting it.
    if (result?.httpStatus === 403 && /already connected/i.test(errorMessage || "")) {
      toast.error(t("linkedin.toast.alreadyConnected", {
        defaultValue: "This account is still marked as connected on the server, so it can't be reconnected yet.",
      }));
      fetchAccounts(true);
      return;
    }
    if (result?.success) {
      clearAccountHint(accountId);
      toast.success(t("linkedin.toast.connectSuccess"));
      fetchAccounts(true);
      return;
    }
    if (status === "CHALLENGE_REQUIRED" || suggestedAction === "submit_challenge" || suggestedAction === "request_email_otp") {
      setAccountHint(accountId, {
        status: "PENDING_VERIFICATION",
        lastSuggestedAction: suggestedAction || "submit_challenge",
        connectionError: errorMessage || "2FA/Challenge Required",
      });
      openPinModal(accountId, challengeType || "EMAIL_PIN", suggestedAction || "submit_challenge");
      fetchAccounts(true);
      return;
    }
    if (suggestedAction === "edit_credentials" || status === "INVALID_CREDENTIALS") {
      setAccountHint(accountId, {
        lastSuggestedAction: "edit_credentials",
        connectionError: errorMessage || t("linkedin.toast.invalidCredentials"),
      });
      toast.error(errorMessage || t("linkedin.toast.invalidCredentials"));
      fetchAccounts(true);
      return;
    }
    if (suggestedAction === "rotate_ip" || status === "PROXY_FAILED") {
      setAccountHint(accountId, {
        lastSuggestedAction: "rotate_ip",
        connectionError: errorMessage || t("linkedin.toast.rotateIpNeeded"),
      });
      toast.error(errorMessage || t("linkedin.toast.rotateIpNeeded"));
      fetchAccounts(true);
      return;
    }
    if (errorMessage) {
      setAccountHint(accountId, { connectionError: errorMessage });
      toast.error(errorMessage);
    } else if (status && status !== "ERROR") {
      toast.error(t("linkedin.toast.error"));
    }
    fetchAccounts(true);
  };

  const openConnectWizard = (existingAccount = null) => {
    showModal(
      existingAccount ? "linkedin.reconnectTitle" : "linkedin.connectTitle",
      <ConnectLinkedInWizard
        account={existingAccount}
        onConnectStarted={(snapshot) => markConnecting(snapshot.id, snapshot)}
        onSuccess={handleConnectResult}
      />
    );
  };

  const openEditModal = (account) => {
    showModal("linkedin.editTitle", <LinkedInAccountModal account={account} onSuccess={() => fetchAccounts()} />);
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm(t("linkedin.deleteConfirm"))) return;
    const original = [...accounts];
    setAccounts(accounts.filter((a) => a.id !== accountId));
    try {
      await deleteLinkedInAccount(accountId);
      toast.success(t("linkedin.toast.deleteSuccess"));
    } catch {
      setAccounts(original);
      toast.error(t("linkedin.toast.error"));
    }
  };

  const handleReconnect = (account) => {
    openConnectWizard(account);
  };

  const handleQuickConnect = async (accountId) => {
    markConnecting(accountId);
    setActionLoadingId(accountId);
    try {
      await waitForProxyReady(accountId);
      const response = await connectLinkedInAccount(accountId);
      handleConnectResult(unwrapConnectResult(response), accountId);
    } catch (err) {
      clearConnecting(accountId);
      notifyError(err, t("linkedin.toast.error"));
      fetchAccounts(true);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRetryProxy = async (accountId) => {
    setActionLoadingId(accountId);
    try {
      await retryLinkedInProxyProvision(accountId);
      toast.success(t("linkedin.toast.proxyRetryQueued"));
      fetchAccounts(true);
    } catch (err) {
      notifyError(err, t("linkedin.toast.error"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDisconnect = async (accountId) => {
    if (!window.confirm(t("linkedin.disconnectConfirm"))) return;
    setActionLoadingId(accountId);
    try {
      await disconnectLinkedInAccount(accountId);
      toast.success(t("linkedin.toast.disconnectSuccess"));
      fetchAccounts();
    } catch {
      toast.error(t("linkedin.toast.error"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleTestConnection = async (accountId) => {
    setActionLoadingId(accountId);
    try {
      await testLinkedInAccountConnection(accountId);
      toast.success(t("linkedin.toast.testSuccess"));
    } catch {
      toast.error(t("linkedin.toast.error"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRotateIp = async (accountId) => {
    setActionLoadingId(accountId);
    try {
      await rotateLinkedInIp(accountId);
      toast.success(t("linkedin.toast.rotateIpSuccess"));
      setActionLoadingId(null);
      clearAccountHint(accountId);
      await handleQuickConnect(accountId);
    } catch (err) {
      toast.error(getErrorMessage(err, t("linkedin.toast.error")));
      setActionLoadingId(null);
    }
  };

  const renderPrimaryAction = (account) => {
    const isConnecting = connectingIds.has(account.id);
    const displayStatus = getDisplayStatus(account, isConnecting);
    const busy = actionLoadingId === account.id || isConnecting;
    if (displayStatus === "CONNECTING" || displayStatus === "PROXY_SETUP") {
      return null;
    }
    if (displayStatus === "PROXY_FAILED") {
      return (
        <button
          onClick={() => handleRetryProxy(account.id)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold text-white bg-orange-500 rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          {t("linkedin.retryProxy")}
        </button>
      );
    }
    if (displayStatus === "PIN_REQUIRED") {
      return (
        <button
          onClick={() =>
            openPinModal(
              account.id,
              account.lastSuggestedAction === "request_email_otp" ? "APP" : "EMAIL_PIN",
              account.lastSuggestedAction || "submit_challenge"
            )
          }
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold text-white bg-purple-600 rounded-lg hover:opacity-90 transition-opacity"
        >
          {t("linkedin.enterPin")}
        </button>
      );
    }
    if (account.lastSuggestedAction === "rotate_ip") {
      return (
        <button
          onClick={() => handleRotateIp(account.id)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold text-white bg-orange-500 rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          {t("linkedin.rotateIp")}
        </button>
      );
    }
    if (displayStatus === "WRONG_CREDENTIALS" || displayStatus === "NOT_CONNECTED" || displayStatus === "NEEDS_ATTENTION") {
      return (
        <button
          onClick={() => handleReconnect(account)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold text-white bg-taction rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          {t("linkedin.reconnect")}
        </button>
      );
    }
    return (
      <button
        onClick={() => openEditModal(account)}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <GearIcon className="w-4 h-4 text-gray-500" />
        {t("linkedin.configureLimits", { defaultValue: "Configure limits" })}
      </button>
    );
  };

  const startIndex = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endIndex = Math.min(pagination.page * pagination.limit, pagination.total);

  if (isLoading && accounts.length === 0) return <LoadingOverlay />;

  return (
    <div className="py-[20px]">
      <h1 className="text-[26px] font-bold text-gray-900 mb-4">{t("linkedin.title")}</h1>

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-tprimary/10 text-gray-600 text-[13px] leading-relaxed rounded-xl px-4 py-4 mb-4">
        <InfoIcon className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
        <p>
          <Trans i18nKey="linkedin.sendersInfoBanner">
            The LinkedIn accounts are called <strong>senders</strong> when put in a campaign. Connect multiple
            LinkedIn sending accounts on one campaign to increase your daily sending volume.
          </Trans>
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Card header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          {accounts.length > 0 || filters.search || filters.status ? (
            <div className="flex flex-wrap items-center gap-3 flex-1">
              <div className="relative flex-1 min-w-[200px] max-w-[280px]">
                <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyFilters({ search: searchValue })}
                  placeholder={t("linkedin.searchPlaceholder", { defaultValue: "Search senders" })}
                  className="w-full pl-10 pr-3 py-2.5 text-[14px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none focus:border-gray-400 transition-colors"
                />
              </div>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    applyFilters({ status: e.target.value });
                  }}
                  className="appearance-none pl-3.5 pr-9 py-2.5 text-[14px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none focus:border-gray-400 min-w-[110px] cursor-pointer"
                >
                  <option value="">{t("linkedin.allStatuses", { defaultValue: "All" })}</option>
                  {statusFilterOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
          ) : (
            <div />
          )}
          <button
            onClick={() => openConnectWizard()}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold px-4 py-2.5 rounded-lg transition-colors shrink-0"
          >
            <LinkedinIconBadge className="w-4 h-4" />
            {t("linkedin.connectAccount")}
          </button>
        </div>

        {error && <div className="px-6 py-3 text-red-500 text-sm">{error}</div>}

        {accounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-20 px-6">
            <EmptyIllustration />
            {filters.search || filters.status ? (
              <>
                <p className="mt-5 text-[15px] font-bold text-gray-900">
                  {t("linkedin.noMatchingAccountsTitle", { defaultValue: "No senders match your search." })}
                </p>
                <p className="mt-1 text-[13px] text-gray-500">
                  {t("linkedin.noMatchingAccountsSubtitle", { defaultValue: "Try a different search or filter." })}
                </p>
              </>
            ) : (
              <>
                <p className="mt-5 text-[15px] font-bold text-gray-900">
                  {t("linkedin.noAccountsTitle", { defaultValue: "You have no LinkedIn accounts yet." })}
                </p>
                <p className="mt-1 text-[13px] text-gray-500">
                  {t("linkedin.noAccountsSubtitle", { defaultValue: "Click on 'Connect account' to start." })}
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[760px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-6 py-3 text-[12px] font-semibold text-gray-500">
                      {t("linkedin.name_col", { defaultValue: "LinkedIn Account" })}
                    </th>
                    <th className="px-4 py-3 text-[12px] font-semibold text-gray-500">{t("linkedin.status_col")}</th>
                    <th className="px-4 py-3 text-[12px] font-semibold text-gray-500">
                      {t("linkedin.sendingLimits_col", { defaultValue: "Sending limits" })}
                    </th>
                    <th className="px-4 py-3"></th>
                    <th className="px-4 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((rawAccount) => {
                    const account = mergeAccount(rawAccount);
                    const isConnecting = connectingIds.has(account.id);
                    const displayStatus = getDisplayStatus(account, isConnecting);
                    const limits = getAccountLimits(account);
                    return (
                      <tr key={account.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60 transition-colors">
                        <td className="px-6 py-4 min-w-[200px]">
                          <div className="flex items-center gap-3">
                            {account.profileImage ? (
                              <img
                                src={account.profileImage}
                                alt={account.accountName}
                                className="w-9 h-9 rounded-full object-cover border border-gray-200 flex-shrink-0"
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-taction text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                                {(account.accountName || account.email || "?").charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-[14px] font-semibold text-gray-900 truncate">{account.accountName || "-"}</p>
                              {account.firstName && (
                                <p className="text-[12px] text-gray-400 truncate">
                                  {account.firstName} {account.lastName}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <StatusBadge displayStatus={displayStatus} />
                          {((account.connectionError && displayStatus !== "AVAILABLE" && displayStatus !== "CONNECTING")
                            || (displayStatus === "PROXY_FAILED" && account.proxyProvisioningError)) && (
                              <p className="text-[11px] text-gray-400 mt-1 line-clamp-2 max-w-[180px]">
                                {displayStatus === "PROXY_FAILED" ? account.proxyProvisioningError : account.connectionError}
                              </p>
                            )}
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Pill icon={<PersonMiniIcon />} value={`${limits.connectionsUsed}/${limits.connectionsTotal}`} />
                            <Pill icon={<EnvelopeMiniIcon />} value={`${limits.messagesUsed}/${limits.messagesTotal}`} />
                          </div>
                        </td>

                        <td className="px-4 py-4">{renderPrimaryAction(account)}</td>

                        <td className="px-4 py-4 text-right">
                          <LinkedInActionsDropdown
                            account={account}
                            displayStatus={displayStatus}
                            onEdit={() => openEditModal(account)}
                            onDelete={() => handleDelete(account.id)}
                            onConnect={() => handleQuickConnect(account.id)}
                            onReconnect={() => handleReconnect(account)}
                            onDisconnect={() => handleDisconnect(account.id)}
                            onTestConnection={() => handleTestConnection(account.id)}
                            onEnterPin={() =>
                              openPinModal(
                                account.id,
                                account.lastSuggestedAction === "request_email_otp" ? "APP" : "EMAIL_PIN",
                                account.lastSuggestedAction || "submit_challenge"
                              )
                            }
                            onRotateIp={() => handleRotateIp(account.id)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
              <div className="text-[13px] text-gray-500">
                {t("campaign.showingPrefix", { defaultValue: "Showing" })}{" "}
                <strong className="text-gray-700 font-semibold">
                  {startIndex}-{endIndex}
                </strong>{" "}
                {t("campaign.showingOf", { defaultValue: "of" })}{" "}
                <strong className="text-gray-700 font-semibold">{pagination.total}</strong>
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={!pagination.hasPrev}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  className="px-3.5 py-1.5 text-[13px] font-medium text-gray-600 bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                >
                  {t("pagination.previous")}
                </button>
                <button
                  disabled={!pagination.hasNext}
                  onClick={() => handlePageChange(pagination.page + 1)}
                  className="px-3.5 py-1.5 text-[13px] font-medium text-gray-600 bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                >
                  {t("pagination.next")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const LinkedinIconBadge = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="16" height="16" rx="3.5" stroke="currentColor" strokeWidth="1.5" />
    <line x1="6.5" y1="8.7" x2="6.5" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="6.5" cy="6" r="0.9" fill="currentColor" />
    <line x1="10" y1="8.7" x2="10" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M10 11.2c0-1.4.8-2.3 1.9-2.3S13.5 9.8 13.5 11.2V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LinkedInActionsDropdown = ({
  account,
  displayStatus,
  onEdit,
  onDelete,
  onConnect,
  onReconnect,
  onDisconnect,
  onTestConnection,
  onEnterPin,
  onRotateIp,
}) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu trigger={<KebabIcon className="w-4 h-4" />}>
      <button onClick={onEdit} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
        {t("linkedin.edit")}
      </button>
      {account.isConnected ? (
        <button onClick={onDisconnect} className="flex items-center w-full px-4 py-2 text-sm text-yellow-700 hover:bg-gray-100">
          {t("linkedin.disconnect")}
        </button>
      ) : displayStatus !== "CONNECTING" ? (
        <>
          <button onClick={onReconnect} className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-gray-100">
            {t("linkedin.reconnect")}
          </button>
          <button onClick={onConnect} className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-gray-100">
            {t("linkedin.connect")}
          </button>
        </>
      ) : null}
      {displayStatus === "PIN_REQUIRED" && (
        <button onClick={onEnterPin} className="flex items-center w-full px-4 py-2 text-sm text-purple-700 hover:bg-gray-100">
          {t("linkedin.enterPin")}
        </button>
      )}
      {account.lastSuggestedAction === "rotate_ip" && (
        <button onClick={onRotateIp} className="flex items-center w-full px-4 py-2 text-sm text-orange-700 hover:bg-gray-100">
          {t("linkedin.rotateIp")}
        </button>
      )}
      <button onClick={onTestConnection} className="flex items-center w-full px-4 py-2 text-sm text-blue-700 hover:bg-gray-100">
        {t("linkedin.testConnection")}
      </button>
      <hr className="my-1" />
      <button onClick={onDelete} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
        {t("linkedin.delete")}
      </button>
    </DropdownMenu>
  );
};

export default LinkedInAccounts;
